import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./ProjectMap.module.css";
import { featured } from "../data/featuredProjects";
import CustomButton from "../components/CustomButton";

type Item = {
  id: number;
  title: string;
  href: string;
  img: string;
};

type Props = {
  title?: string;
  subtitle?: string;
  max?: number;
  allHref?: string;
  ctaLabel?: string;
};

const FALLBACK_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'>
      <defs>
        <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
          <stop offset='0' stop-color='%23d7d2c8'/>
          <stop offset='1' stop-color='%23ebe6de'/>
        </linearGradient>
      </defs>
      <rect width='100%' height='100%' fill='url(#g)'/>
      <g fill='%239c7b56' opacity='.55'>
        <circle cx='60' cy='80' r='6'/>
        <circle cx='110' cy='60' r='5'/>
        <circle cx='140' cy='120' r='7'/>
      </g>
    </svg>`
  );

const pickImage = (p: any): string =>
  (Array.isArray(p.images) && p.images[0]) || p.cover || p.img || FALLBACK_SVG;

const pickHref = (p: any): string => p.href || p.to || `/projects/${p.id ?? ""}`;

const normalize = (src: any[]): Item[] =>
  src
    .filter(Boolean)
    .map((p, idx) => ({
      id: Number.isFinite(Number(p.id)) ? Number(p.id) : idx + 1,
      title: String(p.title ?? "Projekt"),
      href: pickHref(p),
      img: pickImage(p),
    }));


const ProjectMap: React.FC<Props> = ({
  title = `Projektkarte "Highlights"`,
  max = 16,
  allHref = "/projects",
  ctaLabel = "Alle Projekte",
}) => {

  const all: Item[] = useMemo(() => normalize(featured), []);

  const items: Item[] = useMemo(() => {
    const pool = [...all];
    const seen = new Set<number>();
    const res: Item[] = [];
    while (res.length < Math.min(max, pool.length)) {
      const i = (Math.random() * pool.length) | 0;
      if (!seen.has(i)) {
        seen.add(i);
        res.push(pool[i]);
      }
    }
    return res;
  }, [all, max]);

  const stageRef = useRef<HTMLDivElement | null>(null);
  const nodeRefs = useRef<Map<number, HTMLAnchorElement>>(new Map());
  const [centers, setCenters] = useState<{ id: number; x: number; y: number }[]>([]);
  const [hoverId, setHoverId] = useState<number | null>(null);
  const [labelTopSet, setLabelTopSet] = useState<Set<number>>(new Set()); 

  const onImgErr = (e: React.SyntheticEvent<HTMLImageElement>) => {
    (e.currentTarget as HTMLImageElement).src = FALLBACK_SVG;
  };

 
  useEffect(() => {
    if (!stageRef.current) return;

    let frame = 0;
    const measure = () => {
      const stage = stageRef.current;
      if (!stage) return;

      const parentRect = stage.getBoundingClientRect();
      const stageH = parentRect.height;

      const nextCenters: typeof centers = [];
      const nextTop = new Set<number>();

      items.forEach((p) => {
        const nodeEl = nodeRefs.current.get(p.id);
        if (!nodeEl) return;

        const bubbleEl = nodeEl.querySelector(`.${styles.bubble}`) as HTMLElement | null;
        const labelEl = nodeEl.querySelector(`.${styles.labelOutside}`) as HTMLElement | null;

        const r = (bubbleEl ?? nodeEl).getBoundingClientRect();
        const bubbleBottom = r.bottom - parentRect.top; 
        const labelH = labelEl ? labelEl.offsetHeight : 24;

        const spaceBottom = stageH - bubbleBottom;
        const needsTop = spaceBottom < labelH + 10;
        if (needsTop) nextTop.add(p.id);

        nextCenters.push({
          id: p.id,
          x: r.left - parentRect.left + r.width / 2,
          y: r.top - parentRect.top + r.height / 2,
        });
      });

      setCenters(nextCenters);
      setLabelTopSet(nextTop);
    };

    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });

    ro.observe(stageRef.current);
    items.forEach((p) => {
      const el = nodeRefs.current.get(p.id);
      if (el) ro.observe(el);
    });

    frame = requestAnimationFrame(measure);

    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [items]);

  const edges = useMemo(() => {
    if (!centers.length || hoverId == null) return [] as Array<[number, number]>;
    const others = centers.map((c) => c.id).filter((id) => id !== hoverId);
    return others.map(
      (to) => [Math.min(hoverId, to), Math.max(hoverId, to)] as [number, number]
    );
  }, [centers, hoverId]);

  const getC = (id: number) => centers.find((c) => c.id === id);

  return (
    <section className={styles.wrap} aria-labelledby="pm-title">
      <header className={styles.header}>
        <h2 id="pm-title" className={styles.sectionTitle}>{title}</h2>
      </header>

      <div className={styles.stage} ref={stageRef}>

        <svg className={styles.wires} aria-hidden="true">
          {edges.map(([a, b], i) => {
            const ca = getC(a);
            const cb = getC(b);
            if (!ca || !cb) return null;

       
            const dx = (cb.x - ca.x) * 0.35;
            const dy = (cb.y - ca.y) * 0.35;
            const c1x = ca.x + dx;
            const c1y = ca.y + dy;
            const c2x = cb.x - dx;
            const c2y = cb.y - dy;

            return (
              <path
                key={i}
                d={`M ${ca.x},${ca.y} C ${c1x},${c1y} ${c2x},${c2y} ${cb.x},${cb.y}`}
                className={styles.wireHot}
              />
            );
          })}
        </svg>

        <div className={styles.band} role="list">
          {items.map((p, idx) => (
            <a
              key={p.id}
              href={p.href}
              role="listitem"
              className={styles.node}
              ref={(el) => {
                if (el) nodeRefs.current.set(p.id, el);
                else nodeRefs.current.delete(p.id);
              }}
              aria-label={p.title}
              onMouseEnter={() => setHoverId(p.id)}
              onMouseLeave={() => setHoverId(null)}
              onFocus={() => setHoverId(p.id)}
              onBlur={() => setHoverId(null)}
              style={
                { ["--pm-float-dur" as any]: `${0.85 + (idx % 5) * 0.07}s` } as React.CSSProperties
              }
            >
              <div className={styles.bubble}>
                <img
                  className={styles.bubbleImg}
                  src={p.img}
                  alt=""
                  loading="lazy"
                  onError={onImgErr}
                />
              </div>

              <span
                className={`${styles.labelOutside} ${
                  labelTopSet.has(p.id) ? styles.labelTop : ""
                }`}
                aria-hidden="true"
              >
                {p.title}
              </span>
            </a>
          ))}

          <div className={styles.moreDots} aria-hidden="true">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>

      <div className={styles.ctaRow}>
        <CustomButton
          text={ctaLabel}
          to={allHref}
          variant="filled"
          size="medium"
        />
      </div>
    </section>
  );
};

export default ProjectMap;
